// Hierarchical design picker — Material → Category → Product + colour chart.
// Opens as a full-screen dialog so staff can walk clients through the catalog.
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { api } from "@/lib/api";
import { Check, Search, Sparkles, Loader2 } from "lucide-react";

// ---------------- Schematic thumbnail (per infill family) ----------------
// Optional `photos` prop = FENCE_PHOTOS[infill] from /api/design-catalog. When
// provided, a real photo hero appears above the SVG so clients can see the
// actual finished look before diving into schematics.
export const DesignThumb = ({ infill = "slats", colourHex = "#333638", size = 72, photos }) => {
  const heroPhoto = Array.isArray(photos) && photos.length > 0 ? photos[0] : null;
  const W = size * 1.4, H = size;
  const top = H * 0.15, bottom = H * 0.85, left = W * 0.08, right = W * 0.92;
  const parts = [];
  const stroke = colourHex;

  if (infill === "slats") {
    for (let y = top + 4; y < bottom - 2; y += Math.max(4, H * 0.09)) {
      parts.push(<line key={`s${y}`} x1={left} y1={y} x2={right} y2={y} stroke={stroke} strokeWidth={Math.max(2, H * 0.05)} />);
    }
  } else if (infill === "slats_vertical" || infill === "battens") {
    const step = Math.max(4, W * 0.07);
    for (let x = left + 2; x < right; x += step) {
      parts.push(<line key={`sv${x}`} x1={x} y1={top} x2={x} y2={bottom} stroke={stroke} strokeWidth={Math.max(2, W * 0.03)} />);
    }
  } else if (infill === "pickets") {
    const step = Math.max(4, W * 0.09);
    for (let x = left + 4; x < right; x += step) {
      parts.push(<line key={`p${x}`} x1={x} y1={top + 3} x2={x} y2={bottom} stroke={stroke} strokeWidth={Math.max(1.5, W * 0.018)} />);
    }
  } else if (infill === "spear") {
    const step = Math.max(4, W * 0.11);
    for (let x = left + 4; x < right; x += step) {
      parts.push(<line key={`p${x}`} x1={x} y1={top + 3} x2={x} y2={bottom} stroke={stroke} strokeWidth={Math.max(1.5, W * 0.018)} />);
      parts.push(<path key={`sp${x}`} d={`M ${x - 3} ${top + 5} L ${x} ${top - 4} L ${x + 3} ${top + 5} Z`} fill={stroke} />);
    }
  } else if (infill === "ringtop") {
    const step = Math.max(4, W * 0.11);
    for (let x = left + 4; x < right; x += step) {
      parts.push(<line key={`p${x}`} x1={x} y1={top + 6} x2={x} y2={bottom} stroke={stroke} strokeWidth={Math.max(1.5, W * 0.018)} />);
    }
    for (let x = left + 4; x < right - 4; x += step) {
      parts.push(<circle key={`r${x}`} cx={x + step / 2} cy={top + 6} r={step / 3.5} fill="none" stroke={stroke} strokeWidth={1.2} />);
    }
  } else if (infill === "mesh") {
    // Realistic diamond-weave chain-link. Two crossing diagonal grids give
    // the characteristic linked-diamond look instead of the plain square
    // grid that used to render (which Trent flagged as "shit — looks
    // nothing like chain mesh").
    const step = Math.max(6, W * 0.09);
    const w = right - left;
    const h = bottom - top;
    for (let d = -h; d < w + h; d += step) {
      parts.push(<line key={`md1${d}`} x1={left + d} y1={top} x2={left + d + h} y2={bottom} stroke={stroke} strokeWidth={0.9} opacity={0.85} />);
      parts.push(<line key={`md2${d}`} x1={left + d} y1={bottom} x2={left + d + h} y2={top} stroke={stroke} strokeWidth={0.9} opacity={0.85} />);
    }
    // Twist knots where the strands cross — sold detail that reads as chain-link at a glance
    for (let x = left + step / 2; x < right; x += step) {
      for (let y = top + step / 2; y < bottom; y += step) {
        parts.push(<circle key={`kn${x}-${y}`} cx={x} cy={y} r={0.6} fill={stroke} />);
      }
    }
    // Top selvedge wire — chain-link fences always have this rolled edge
    parts.push(<line key="msel" x1={left} y1={top} x2={right} y2={top} stroke={stroke} strokeWidth={Math.max(1.4, H * 0.025)} />);
  } else if (infill === "corrugated") {
    // Corromesh — vertical corrugated Colorbond sheet. Alternating light/dark
    // bands with subtle highlights so it reads as a corrugated iron fence,
    // NOT flat sheet. Matches AU industry appearance of Colorbond corrugated.
    parts.push(<rect key="corr-bg" x={left} y={top} width={right - left} height={bottom - top} fill={stroke} opacity={0.94} />);
    const pitch = Math.max(6, W * 0.055);
    for (let x = left; x < right; x += pitch) {
      // Shadow trough
      parts.push(<rect key={`ct${x}`} x={x} y={top} width={pitch * 0.35} height={bottom - top} fill="rgba(0,0,0,0.28)" />);
      // Highlight crest — thin bright line, faked by 1px offset
      parts.push(<line key={`ch${x}`} x1={x + pitch * 0.7} y1={top} x2={x + pitch * 0.7} y2={bottom} stroke="rgba(255,255,255,0.35)" strokeWidth={0.9} />);
    }
    // Capping rail top
    parts.push(<rect key="corr-cap" x={left} y={top - 2} width={right - left} height={3} fill={stroke} />);
  } else if (infill === "sheet") {
    parts.push(<rect key="sh" x={left} y={top} width={right - left} height={bottom - top} fill={stroke} opacity={0.92} />);
    for (let x = left + 4; x < right; x += 5) {
      parts.push(<line key={`c${x}`} x1={x} y1={top} x2={x} y2={bottom} stroke="rgba(255,255,255,0.12)" strokeWidth={1} />);
    }
  } else if (infill === "louvres") {
    for (let y = top + 3; y < bottom - 2; y += Math.max(3, H * 0.11)) {
      parts.push(<path key={`l${y}`} d={`M ${left} ${y + 2} Q ${(left + right) / 2} ${y - 4} ${right} ${y + 2}`} fill="none" stroke={stroke} strokeWidth={Math.max(1.5, H * 0.04)} />);
    }
  } else if (infill === "curved") {
    const step = Math.max(4, W * 0.09);
    for (let x = left + 4; x < right; x += step) {
      parts.push(<line key={`c${x}`} x1={x} y1={top + 6} x2={x} y2={bottom} stroke={stroke} strokeWidth={Math.max(1.5, W * 0.018)} />);
    }
    parts.push(<path key="curved-top" d={`M ${left} ${top + 8} Q ${(left + right) / 2} ${top - 6} ${right} ${top + 8}`} fill="none" stroke={stroke} strokeWidth={Math.max(2, H * 0.04)} />);
  } else if (infill === "paling") {
    const step = Math.max(4, W * 0.08);
    for (let x = left; x < right; x += step) {
      parts.push(<rect key={`pl${x}`} x={x} y={top} width={step - 1} height={bottom - top} fill={stroke} opacity={0.9} />);
    }
  } else if (infill === "rail") {
    for (let y = top + H * 0.25; y < bottom; y += H * 0.28) {
      parts.push(<line key={`r${y}`} x1={left} y1={y} x2={right} y2={y} stroke={stroke} strokeWidth={Math.max(3, H * 0.06)} />);
    }
  }

  // Posts
  const postW = Math.max(3, W * 0.035);
  const svg = (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full bg-gradient-to-b from-sky-50/60 to-slate-100 rounded-sm">
      <line x1={0} y1={bottom + 2} x2={W} y2={bottom + 2} stroke="#94a3b8" strokeWidth={1.5} />
      {parts}
      <rect x={left - postW / 2} y={top - 2} width={postW} height={bottom - top + 4} fill={stroke} rx={0.5} />
      <rect x={right - postW / 2} y={top - 2} width={postW} height={bottom - top + 4} fill={stroke} rx={0.5} />
      {infill !== "sheet" && infill !== "rail" && infill !== "louvres" && (
        <line x1={left} y1={top + 2} x2={right} y2={top + 2} stroke={stroke} strokeWidth={Math.max(1.5, H * 0.03)} />
      )}
    </svg>
  );

  // If a real photo is available, show it as a hero above the schematic. Keeps
  // the schematic underneath so clients can compare colour/design detail.
  if (heroPhoto) {
    return (
      <div className="flex flex-col gap-1 w-full h-full" data-testid={`fence-photo-${infill}`}>
        <div className="relative flex-[3] rounded-sm overflow-hidden border border-slate-200">
          <img src={heroPhoto.src} alt={heroPhoto.caption || infill}
               className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute bottom-0 left-0 right-0 bg-black/45 text-white text-[9px] px-1.5 py-0.5 leading-tight truncate">
            Real install · {heroPhoto.caption || ""}
          </div>
        </div>
        <div className="flex-[2] min-h-[24px]">{svg}</div>
      </div>
    );
  }
  return svg;
};

// ---------------- Colour palette grouped by family ----------------
const ColourGrid = ({ colours, value, onPick }) => {
  const groups = useMemo(() => {
    const m = new Map();
    for (const c of colours || []) {
      if (!m.has(c.family)) m.set(c.family, []);
      m.get(c.family).push(c);
    }
    return Array.from(m.entries());
  }, [colours]);
  return (
    <div className="space-y-3">
      {groups.map(([family, list]) => (
        <div key={family}>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">{family}</div>
          <div className="flex flex-wrap gap-1.5">
            {list.map((c) => {
              const active = value?.name === c.name;
              return (
                <button key={c.name} type="button" onClick={() => onPick(c)} title={c.name}
                  className={`relative w-9 h-9 rounded-sm border-2 transition-transform hover:scale-110 ${active ? "border-cyan-500 ring-2 ring-cyan-200" : "border-slate-200"}`}
                  style={{ background: c.hex }}
                  data-testid={`design-colour-${c.name.replace(/\s+/g, "-").toLowerCase()}`}>
                  {active && <Check size={14} className="absolute inset-0 m-auto text-white drop-shadow" />}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

// ---------------- Main picker ----------------
export const DesignPicker = ({ open, onOpenChange, value, onChange, onGenerate, generating }) => {
  const { data, isLoading } = useQuery({
    queryKey: ["design-catalog"],
    queryFn: async () => (await api.get("/design-catalog")).data,
    enabled: open,
    staleTime: 10 * 60 * 1000,
  });
  const materials = data?.materials || [];
  const colours = data?.colours || [];
  const photos = data?.photos || {};

  const [matId, setMatId] = useState(null);
  const [catId, setCatId] = useState(null);
  const [q, setQ] = useState("");

  const activeMat = matId ? materials.find((m) => m.id === matId) : materials[0];
  const activeCat = activeMat && (catId ? activeMat.categories.find((c) => c.id === catId) : activeMat.categories[0]);
  const search = q.trim().toLowerCase();

  const products = useMemo(() => {
    if (search) {
      const hits = [];
      for (const m of materials) for (const c of m.categories) for (const p of c.products) {
        if (`${p.label} ${p.blurb || ""} ${m.label} ${c.label}`.toLowerCase().includes(search)) {
          hits.push({ ...p, material: m.label, category: c.label });
        }
      }
      return hits.slice(0, 60);
    }
    return (activeCat?.products || []).map((p) => ({ ...p, material: activeMat?.label, category: activeCat?.label }));
  }, [search, materials, activeMat, activeCat]);

  const chosenColour = value?.colour_hex ? { name: value.colour_name, hex: value.colour_hex } : colours[0];

  const pickProduct = (p) => {
    onChange?.({
      ...value,
      design: p.id,
      design_label: p.label,
      infill: p.infill,
      colour_name: value?.colour_name || chosenColour?.name || "Monument",
      colour_hex: value?.colour_hex || chosenColour?.hex || "#333638",
    });
  };
  const pickColour = (c) => onChange?.({ ...value, colour_name: c.name, colour_hex: c.hex });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-[95vw] p-0 rounded-sm max-h-[92vh] overflow-hidden flex flex-col" data-testid="design-picker-dialog">
        <DialogHeader className="px-5 pt-4 pb-3 border-b border-slate-100">
          <DialogTitle className="text-cyan-700">CCFI Design Library</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Pick a material, a category, then a specific product — plus a Colorbond® or powdercoat colour. The AI visualiser will place it into your customer's photo.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="p-10 text-center text-slate-400 text-sm">Loading design library…</div>
        ) : (
          <div className="flex flex-1 min-h-0">
            {/* Sidebar — materials + categories */}
            <div className="w-56 border-r border-slate-100 bg-slate-50/50 shrink-0 flex flex-col">
              <div className="p-2 border-b border-slate-100">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 px-1">Material</div>
                <div className="flex flex-col gap-1">
                  {materials.map((m) => (
                    <button key={m.id} onClick={() => { setMatId(m.id); setCatId(null); setQ(""); }}
                      className={`text-left text-sm px-2 py-1.5 rounded-sm transition ${activeMat?.id === m.id ? "bg-cyan-600 text-white" : "hover:bg-slate-100 text-slate-700"}`}
                      data-testid={`design-material-${m.id}`}>{m.label}</button>
                  ))}
                </div>
              </div>
              <ScrollArea className="flex-1">
                <div className="p-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 px-1">Category</div>
                  <div className="flex flex-col gap-0.5">
                    {(activeMat?.categories || []).map((c) => (
                      <button key={c.id} onClick={() => { setCatId(c.id); setQ(""); }}
                        className={`text-left text-xs px-2 py-1.5 rounded-sm transition ${activeCat?.id === c.id ? "bg-white border border-cyan-200 text-cyan-700 font-semibold" : "hover:bg-slate-100 text-slate-600"}`}
                        data-testid={`design-category-${c.id}`}>{c.label}</button>
                    ))}
                  </div>
                </div>
              </ScrollArea>
            </div>

            {/* Products grid + colour panel */}
            <div className="flex-1 min-w-0 flex flex-col">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                  <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search all designs…"
                    className="rounded-sm h-9 pl-8" data-testid="design-search-input" />
                </div>
                {value?.design && (
                  <Badge className="bg-cyan-50 border border-cyan-200 text-cyan-700 rounded-sm font-normal">
                    {value.design_label || value.design.replace(/_/g, " ")}
                  </Badge>
                )}
              </div>

              <div className="flex-1 grid grid-cols-1 md:grid-cols-[1fr_240px] min-h-0">
                <ScrollArea className="min-h-0">
                  <div className="p-4">
                    {!search && activeCat?.blurb && (
                      <p className="text-xs text-slate-500 mb-3">{activeCat.blurb}</p>
                    )}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                      {products.map((p) => {
                        const active = value?.design === p.id;
                        return (
                          <button key={p.id} type="button" onClick={() => pickProduct(p)}
                            className={`text-left rounded-sm border-2 p-2 transition ${active ? "border-cyan-500 bg-cyan-50/40" : "border-slate-200 hover:border-slate-300"}`}
                            data-testid={`design-product-${p.id}`}>
                            <div className="aspect-[4/3] w-full mb-2 rounded-sm overflow-hidden border border-slate-100">
                              <DesignThumb infill={p.infill} colourHex={chosenColour?.hex || "#333638"} size={90} photos={photos[p.infill]} />
                            </div>
                            <div className="text-[11px] font-semibold text-slate-800 leading-tight line-clamp-2">{p.label}</div>
                            {search && <div className="text-[10px] text-slate-400 mt-0.5 truncate">{p.material} · {p.category}</div>}
                            {!search && p.blurb && <div className="text-[10px] text-slate-500 mt-0.5 line-clamp-2">{p.blurb}</div>}
                          </button>
                        );
                      })}
                    </div>
                    {products.length === 0 && <p className="text-xs text-slate-400 text-center py-8">No products match "{q}".</p>}
                  </div>
                </ScrollArea>

                <div className="border-l border-slate-100 bg-slate-50/40 flex flex-col min-h-0">
                  <div className="px-4 py-3 border-b border-slate-100">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Colour</div>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="w-6 h-6 rounded-sm border border-slate-300" style={{ background: chosenColour?.hex }} />
                      <div className="text-sm font-semibold text-slate-800 truncate">{chosenColour?.name || "—"}</div>
                    </div>
                  </div>
                  <ScrollArea className="flex-1">
                    <div className="p-3">
                      <ColourGrid colours={colours} value={chosenColour} onPick={pickColour} />
                    </div>
                  </ScrollArea>
                </div>
              </div>

              <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between gap-2 flex-wrap">
                <div className="text-xs text-slate-500">
                  {value?.design ? <>Selected: <b>{value.design_label || value.design}</b> · <b>{chosenColour?.name}</b></> : "Pick a design and colour to visualise."}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm h-9" data-testid="design-picker-close">Close</Button>
                  {onGenerate && (
                    <Button onClick={onGenerate} disabled={!value?.design || generating}
                      className="rounded-sm h-9 bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="design-picker-generate">
                      {generating ? <><Loader2 size={13} className="animate-spin mr-1.5" /> Rendering…</>
                                  : <><Sparkles size={13} className="mr-1.5" /> Generate AI preview</>}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DesignPicker;
